import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import Footer from '../components/common/Footer';
import SiteHeader from '../components/common/SiteHeader';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const faqs: FAQItem[] = [
    {
      category: 'General',
      question: 'What is an Equity Line of Credit (ELOC)?',
      answer: 'An Equity Line of Credit is a flexible financing arrangement that allows publicly traded companies to access capital by selling shares at their discretion over a specified period. Unlike traditional equity offerings, ELOCs provide companies with the right, but not the obligation, to draw funds when needed.'
    },
    {
      category: 'General',
      question: 'How does Twenty Four Capital differ from traditional investment banks?',
      answer: 'Twenty Four Capital specializes in providing flexible, technology-driven equity financing solutions with same-day pricing capabilities, automated compliance, and no underwriting fees. We focus on speed, flexibility, and cost-efficiency compared to traditional 3-6 month equity raise processes.'
    },
    {
      category: 'Eligibility',
      question: 'What companies qualify for an ELOC?',
      answer: 'Publicly traded companies listed on major exchanges (NYSE, NASDAQ, etc.) with current SEC reporting status typically qualify. We work with micro-cap, small-cap, and mid-cap companies across various industries.'
    },
    {
      category: 'Eligibility',
      question: 'Do we need to be S-3 eligible?',
      answer: 'While S-3 eligibility provides additional flexibility, we have structures available for companies at various stages of their public company lifecycle, including those not yet S-3 eligible.'
    },
    {
      category: 'Process',
      question: 'How long does it take to set up an ELOC?',
      answer: 'Unlike traditional equity offerings that take 3-6 months, an ELOC can typically be established in 2-4 weeks, depending on the complexity and company readiness.'
    },
    {
      category: 'Process',
      question: 'What is the typical facility size?',
      answer: 'Facility sizes vary based on company size and needs: Micro-cap companies ($5M-$25M), Small-cap companies ($20M-$100M), and Mid-cap companies ($50M-$150M+).'
    },
    {
      category: 'Costs',
      question: 'What are the costs associated with an ELOC?',
      answer: 'ELOCs eliminate traditional underwriting fees (4-7%). Costs include setup fees, modest market discounts on share issuances, and legal documentation. Overall costs are typically 50-70% lower than traditional offerings.'
    },
    {
      category: 'Costs',
      question: 'How does pricing work?',
      answer: 'Pricing can be based on same-day market prices or Volume Weighted Average Price (VWAP) over a specified period. Discounts typically range from 0.5-10% depending on company size and market conditions.'
    },
    {
      category: 'Regulatory',
      question: 'What is the Exchange Cap?',
      answer: 'The Exchange Cap limits issuances to 19.99% of outstanding shares without shareholder approval. Our technology automatically tracks and ensures compliance with this limitation.'
    },
    {
      category: 'Regulatory',
      question: 'How do Baby Shelf limitations affect us?',
      answer: 'Companies with public floats under $75 million face Baby Shelf limitations restricting issuances to one-third of public float over 12 months. Our structures help navigate these limitations effectively.'
    }
  ];

  const categories = ['all', ...Array.from(new Set(faqs.map(faq => faq.category)))];
  
  const filteredFAQs = selectedCategory === 'all' 
    ? faqs 
    : faqs.filter(faq => faq.category === selectedCategory);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <>
      <SiteHeader />
      <div className="min-h-screen bg-gradient-financial">
        {/* Hero Section */}
        <section className="relative py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1 
              className="text-5xl font-bold text-primary-950 mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Frequently Asked Questions
            </motion.h1>
            <motion.p 
              className="text-xl text-primary-700"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              Find answers to common questions about equity financing and our services
            </motion.p>
          </div>
        </section>

        {/* Category Filter */}
        <section className="py-8 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-wrap justify-center gap-2">
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedCategory === category
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {category === 'all' ? 'All Categories' : category}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ List */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-4">
              {filteredFAQs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-lg shadow-md overflow-hidden"
                >
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex-1">
                      <span className="text-xs font-medium text-primary-600 uppercase tracking-wide">
                        {faq.category}
                      </span>
                      <h3 className="text-lg font-semibold text-gray-900 mt-1">
                        {faq.question}
                      </h3>
                    </div>
                    {openIndex === index ? (
                      <ChevronUpIcon className="h-5 w-5 text-primary-600 flex-shrink-0" />
                    ) : (
                      <ChevronDownIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    )}
                  </button>
                  
                  {openIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="px-6 pb-5"
                    >
                      <p className="text-gray-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-orange-50">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-primary-950 mb-4">
              Still Have Questions?
            </h2>
            <p className="text-lg text-primary-700 mb-8">
              Our team is here to help you understand how equity financing can work for your company.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-orange-500 text-white font-medium rounded-lg hover:bg-orange-600 transition-colors"
              >
                Contact Us
              </Link>
              <Link
                to="/check-eligibility"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-orange-500 font-medium rounded-lg border-2 border-orange-500 hover:bg-orange-50 transition-colors"
              >
                Check Eligibility
              </Link>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default FAQ; 